import React from "react";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Switch, Tooltip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import CustomTabs from "components/Shared/CustomTabs";
import { toast } from "react-toastify";
import {
  useLazyGetDistrictListQuery,
  useUpdateDistrictStatusMutation,
} from "store/services/LocationsApi/districtApi";

const DistrictListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Districts" }];

  const { isSystemAdmin } = useAuth();
  const activeTab = searchParams.get("tab") || "Active";

  const [updateDistrictStatus] = useUpdateDistrictStatusMutation();

  const districtListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];
  // status update
  const handleStatusChange = (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";

    const isConfirmed = window.confirm(
      `Are you sure you want to change the status to ${newStatus}?`
    );

    if (isConfirmed) {
      updateDistrictStatus({ id: row._id, status: newStatus })
        .unwrap()
        .then(() => {
          toast.success(`District status updated to ${newStatus}`);
        })
        .catch((error) => {
          const errorMessage = error?.data?.message || "Something went wrong!";
          toast.error(errorMessage);
        });
    }
  };

  const [
    fetchDistricts,
    {
      data: districtRes,
      isLoading: loadingDistricts,
      isFetching: fetchingDistricts,
      error,
    },
  ] = useLazyGetDistrictListQuery();

  const columns = [
    {
      name: "District Name",
      selector: (row) => row?.name,
      cell: (row) =>
        row?.name ? (
          <HighlightedText text={row?.name} highlight={keyword} />
        ) : (
          "N/A"
        ),
      sortable: true,
    },
    {
      name: " District Code",
      selector: (row) => row?.code,
      sortable: true,
    },
    {
      name: "Region",
      selector: (row) => row?.region?.name,
      sortable: true,
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/district/${row._id}/detail`}>
            <Tooltip title={"View Detail"} arrow>
              <IconButton size="small" aria-label="View Branch" color="primary">
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Link>
          <Link
            to={`/app/crm/district/${row._id}/edit`}
            state={{ from: "list" }}
          >
            <Tooltip title={"Edit"} arrow>
              <IconButton size="small" aria-label="Edit Branch">
                {/* <ModeEditOutlineTwoTone /> */}
              </IconButton>
            </Tooltip>
          </Link>

          {false && (
            <Tooltip
              title={row.status === "Active" ? "Deactivate" : "Activate"}
              arrow
            >
              <Switch
                checked={row.status === "Active"}
                onChange={() => handleStatusChange(row)}
                color="primary"
                inputProps={{ "aria-label": "Status Toggle" }}
              />
            </Tooltip>
          )}
        </>
      ),
    },
  ];
  React.useEffect(() => {
    fetchDistricts({
      status: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { search: keyword }),
    });
  }, [activeTab, fetchDistricts, currentPage, keyword, perPage]);

  useAppMeta(`${activeTab} Districts`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={isSystemAdmin && <PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={districtListTabs} activeTab={activeTab} />

        <>
          <TableFilter
            searchLabel="Name"
            disabled={loadingDistricts || fetchingDistricts}
          >
            <div className="font-medium">{activeTab} Districts</div>
          </TableFilter>
          <AppTable
            columns={columns}
            data={districtRes?.data ?? []}
            progressPending={loadingDistricts || fetchingDistricts}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={districtRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const PageActions = () => {
  return (
    <div className="flex space-x-4">
      <Link to="/app/crm/district/create">
        <Button variant="contained" startIcon={<Add />}>
          New District
        </Button>
      </Link>
    </div>
  );
};

export default DistrictListScreen;
